import { z } from 'zod';
import type { MovingItemView } from './room-observing.types';
import type { MovingMethodView, MovingSiteView, Packing, CustomerRequests } from './intake.types';
import type { MovingTypeIntakeView } from './moving-type-intake.types';

// ============================================================================
// StructuredDepartureAddress
// ============================================================================

/** Response departureAddress.floorOrUnit - 스펙: { type, value } */
export interface DepartureAddressFloorOrUnit {
  type: 'Floor' | 'Unit';
  value: string;
}

/** 구조화된 출발지 주소 (Response - 상담 시작 후 저장) */
export interface StructuredDepartureAddress {
  roadAddress: string;
  buildingName: string | null;
  dong: string | null;
  floorOrUnit: DepartureAddressFloorOrUnit | null;
}

/** 구조화된 출발지 주소 → 표시 문자열 (동→접미어 동, 층→접미어 층, 호→접미어 호) */
export function formatStructuredAddress(addr: StructuredDepartureAddress): string {
  const parts = [addr.roadAddress];
  if (addr.buildingName) parts.push(addr.buildingName);
  if (addr.dong) parts.push(`${addr.dong}동`);
  const fu = addr.floorOrUnit;
  if (fu != null) {
    parts.push(fu.type === 'Floor' ? `${fu.value}층` : `${fu.value}호`);
  }
  return parts.join(' ');
}

/** 구조화된 출발지 주소 → 2줄 (도로명주소 / 건물명 포함) */
export function formatStructuredAddressTwoLine(addr: StructuredDepartureAddress): {
  primaryLine: string;
  secondaryLine: string;
} {
  const secondaryParts: string[] = [];
  if (addr.buildingName) secondaryParts.push(addr.buildingName);
  if (addr.dong) secondaryParts.push(`${addr.dong}동`);
  const fu = addr.floorOrUnit;
  if (fu != null) {
    secondaryParts.push(fu.type === 'Floor' ? `${fu.value}층` : `${fu.value}호`);
  }
  return {
    primaryLine: addr.roadAddress,
    secondaryLine: secondaryParts.join(' '),
  };
}

/** 상담 시작 API 요청 (departureAddress) - StructuredDepartureAddressRequest: floor/unit 둘 중 하나 필수, 나머지 null */
export interface StartVisitCounselDepartureAddress {
  roadAddress: string;
  buildingName?: string | null;
  dong?: string | null;
  floor: string | null;
  unit: string | null;
}

// ============================================================================
// VisitCounselView
// ============================================================================

/** 방문 상담 상태 (enum-like) */
export const VisitCounselState = {
  APPOINTED: 'APPOINTED',
  STARTED: 'STARTED',
  COMPLETED: 'COMPLETED',
} as const;
export type VisitCounselState = (typeof VisitCounselState)[keyof typeof VisitCounselState];

/**
 * VisitCounselView 타입
 * 방문 상담 조회 뷰
 */
export interface VisitCounselView {
  id: string; // VisitCounselId (UUID)
  orderId: string; // OrderId (UUID)
  counselorId: string; // PlannerId (UUID)
  recipientId: string; // CustomerId (UUID)
  /** 검색 응답 등에서 생략될 수 있음 */
  managerId?: string; // OperatorId (UUID)
  serviceRegionId: string; // ServiceRegionId (UUID)
  scheduleDate: string; // LocalDate (YYYY-MM-DD)
  scheduleTime: string; // LocalTime (HH:mm 또는 HH:mm:ss)
  location: string; // Location value class (String)
  supplyArea: number; // Pyeong value class (Int)
  memo: string;
  createdAt: string; // OffsetDateTime (ISO 8601)
  updatedAt: string; // OffsetDateTime (ISO 8601)
  state?: VisitCounselState; // APPOINTED(배정됨), STARTED(시작됨), COMPLETED(종료됨)
  departureAddress?: StructuredDepartureAddress | null; // 상담 시작 후에만 존재
  departureHasElevator?: boolean | null;
  departureMovingMethod?: MovingMethodView | null;
  arrivalAddress?: StructuredDepartureAddress | null;
  arrivalSupplyArea?: number | null;
  arrivalHasElevator?: boolean | null;
  arrivalMovingMethod?: MovingMethodView | null;
  packing?: Packing | null;
  customerRequests?: CustomerRequests | null;
  movingDate?: string | null; // "YYYY-MM-DD"
  startedAt?: string | null; // 상담 시작 시각 (ISO 8601)
  reviewedAt?: string | null; // 세션 리뷰 완료 시각 (ISO 8601)
  completedAt?: string | null; // 상담 종료 시각 (ISO 8601)
  /** 세션 정보 (스케치, 인테이크 등) */
  sessions?: VisitCounselSessions | null;
  /** 이사 유형 인테이크 (다중 선택 + 유형별 속성 문자열 맵) */
  movingTypeIntake?: MovingTypeIntakeView | null;
}

/** 스케치 세션 방 정보 (SketchedRoomView) - roomId는 API 응답에서 제공 */
export interface SketchingSessionRoom {
  roomId?: string;
  type: string;
  namePrefix: string;
  /** 전역 스케치 순번(도메인). UI 라벨은 displayLabel·ordinalWithinType 우선 */
  index: number;
  /** 스케치 방 목록에서의 순서(1부터). 보통 index와 동일 */
  sketchOrder?: number;
  /** 동일 방 타입(RoomType) 안에서의 순번(1부터) */
  ordinalWithinType?: number;
  /** 화면용 라벨 (예: 1번방, 1번거실) */
  displayLabel?: string;
  isAdditionalSpace?: boolean;
}

/** 스케치 세션 (API: sessions.sketching, id만 반환, sessionId 없음) */
export interface SketchingSession {
  id: string;
  status: 'REQUESTED' | 'STARTED' | 'COMPLETE';
  rooms: SketchingSessionRoom[];
  video: string | null;
}

/** 인테이크 세션 (sessions.intake - departure/arrival 등 단일 소스) */
export interface IntakeSession {
  id: string;
  status: 'REQUESTED' | 'STARTED' | 'COMPLETED';
  departure?: MovingSiteView | null;
  arrival?: MovingSiteView | null;
  packing?: Packing | null;
  customerRequests?: CustomerRequests | null;
  movingDate?: string | null;
  /** PUT intake-session/moving-type 이후 intake 세션에 mirror (플로우 step 완료 판정용) */
  movingTypeIntake?: MovingTypeIntakeView | null;
}

/** 방 옵저빙 세션 (RoomObservingSessionView) */
export interface RoomObservingSession {
  id: string;
  roomId: string;
  status: string;
  /** 방 목록(rooms) 컨텍스트가 있을 때만 채워질 수 있음 */
  sketchOrder?: number | null;
  ordinalWithinType?: number | null;
  displayLabel?: string | null;
  video?: string | null;
  /** specId만 있는 레거시 필드. movingItems 없을 때 폴백용 */
  movingItemSpecIds?: string[];
  /** specId + attributes 포함. 수정 시 기존 속성 로드용 (MovingItemView) */
  movingItems?: MovingItemView[];
}

/** 추가공간 옵저빙 세션 (AdditionalSpaceObservingSessionView) */
export interface AdditionalSpaceObservingSession {
  id: string;
  spaceType: string;
  /** true일 때만 additional-space-observing moving-items API 사용 가능 (세대창고 등) */
  canRecordMovingItems?: boolean;
  /** SpaceType.isAdditionalSpace와 동일 메타 */
  isAdditionalSpace?: boolean;
  status: string;
  video?: string | null;
  roomId?: string | null;
  movingItemSpecIds?: string[];
  movingItems?: MovingItemView[];
}

/** 방문 상담 세션 묶음 (VisitCounselSessionsView) */
export interface VisitCounselSessions {
  sketching?: SketchingSession | null;
  intake?: IntakeSession | null;
  roomObserving?: RoomObservingSession[];
  additionalSpaceObserving?: AdditionalSpaceObservingSession[];
}

// ============================================================================
// VisitCounselSearch Types
// ============================================================================

/**
 * VisitCounselSearchField enum
 */
export type VisitCounselSearchField =
  | 'visitCounselId'
  | 'visitCounselIds'
  | 'orderId'
  | 'counselorId'
  | 'recipientId'
  | 'managerId'
  | 'scheduleDate'
  | 'scheduleTime'
  | 'location'
  | 'supplyArea'
  | 'memo'
  | 'createdAt'
  | 'updatedAt'
  | 'counselorPhone'
  | 'recipientPhone'
  | 'counselorName'
  | 'recipientName'
  | 'managerName';

/**
 * FilterOperator enum
 */
export type FilterOperator =
  | 'CONTAINS'
  | 'EQUALS'
  | 'IN'
  | 'RANGE_GTE'
  | 'RANGE_LTE'
  | 'ANCESTORS'
  | 'DESCENDANTS'
  | 'MATCH';

/**
 * FilterGroupLogic enum
 */
export type FilterGroupLogic = 'AND' | 'OR';

/**
 * VisitCounselSearchFilter 타입
 */
export interface VisitCounselSearchFilter {
  field: VisitCounselSearchField;
  operator: FilterOperator;
  value: string | null;
  values: string[] | null;
}

/**
 * VisitCounselSearchFilterGroup 타입
 */
export interface VisitCounselSearchFilterGroup {
  logic: FilterGroupLogic;
  filters: readonly VisitCounselSearchFilter[];
}

/**
 * VisitCounselSortByField enum
 */
export type VisitCounselSortByField = 'createdAt' | 'scheduleDate' | 'id';

/**
 * VisitCounselSearchQuery 타입
 */
export interface VisitCounselSearchQuery {
  filterGroups: VisitCounselSearchFilterGroup[];
  sortBy: VisitCounselSortByField;
  sortOrder: 'ASC' | 'DESC';
  pageSize: number;
  cursor?: string | null;
}

/**
 * VisitCounselSearchView 타입
 */
export interface VisitCounselSearchView {
  items: VisitCounselView[];
  hasNext: boolean;
  nextCursor: string | null;
  count: number;
}

// ============================================================================
// VisitCounselEventView
// ============================================================================

/**
 * VisitCounselEventViewItem 타입
 * 이벤트 + checkpoint 래퍼 (New 스펙)
 */
export interface VisitCounselEventViewItem {
  event: VisitCounselEvent;
  checkpoint: number;
}

/**
 * VisitCounselEventView 타입
 * 방문 상담 이벤트 조회 뷰
 */
export interface VisitCounselEventView {
  visitCounselId: string; // VisitCounselId (UUID)
  events: VisitCounselEventViewItem[];
}

/**
 * VisitCounselEvent 타입
 * 방문 상담 이벤트 (일반적인 이벤트 구조)
 */
export interface VisitCounselEvent {
  id: string; // EventId
  eventType: string;
  occurredAt: string; // OffsetDateTime (ISO 8601)
  aggregateId: string; // VisitCounselId (UUID)
  sequence: number;
  [key: string]: any; // 이벤트별 추가 필드
}

// ============================================================================
// Zod Schemas
// ============================================================================

/**
 * VisitCounselView 스키마
 */
export const visitCounselViewSchema = z.object({
  id: z.string().uuid(),
  orderId: z.string().uuid(),
  counselorId: z.string().uuid(),
  recipientId: z.string().uuid(),
  managerId: z.string().uuid().optional(),
  serviceRegionId: z.string().uuid(),
  scheduleDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'scheduleDate는 YYYY-MM-DD 형식이어야 합니다.'),
  // API는 HH:mm 또는 HH:mm:ss(LocalTime) 반환
  scheduleTime: z
    .string()
    .regex(/^\d{2}:\d{2}(:\d{2})?$/, 'scheduleTime은 HH:mm 또는 HH:mm:ss 형식이어야 합니다.'),
  location: z.string(),
  supplyArea: z.number().int().nonnegative(),
  memo: z.string(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

/**
 * VisitCounselSearchField 스키마
 */
export const visitCounselSearchFieldSchema = z.enum([
  'visitCounselId',
  'visitCounselIds',
  'orderId',
  'counselorId',
  'recipientId',
  'managerId',
  'scheduleDate',
  'scheduleTime',
  'location',
  'supplyArea',
  'memo',
  'createdAt',
  'updatedAt',
  'counselorPhone',
  'recipientPhone',
  'counselorName',
  'recipientName',
  'managerName',
]);

/**
 * FilterOperator 스키마
 */
export const filterOperatorSchema = z.enum([
  'CONTAINS',
  'EQUALS',
  'IN',
  'RANGE_GTE',
  'RANGE_LTE',
  'ANCESTORS',
  'DESCENDANTS',
  'MATCH',
]);

/**
 * FilterGroupLogic 스키마
 */
export const filterGroupLogicSchema = z.enum(['AND', 'OR']);

/**
 * VisitCounselSearchFilter 스키마
 */
export const visitCounselSearchFilterSchema = z.object({
  field: visitCounselSearchFieldSchema,
  operator: filterOperatorSchema,
  value: z.string().nullable(),
  values: z.array(z.string()).nullable(),
});

/**
 * VisitCounselSearchFilterGroup 스키마
 */
export const visitCounselSearchFilterGroupSchema = z.object({
  logic: filterGroupLogicSchema,
  filters: z.array(visitCounselSearchFilterSchema),
});

/**
 * VisitCounselSortByField 스키마
 */
export const visitCounselSortByFieldSchema = z.enum(['createdAt', 'scheduleDate', 'id']);

/**
 * VisitCounselSearchQuery 스키마
 */
export const visitCounselSearchQuerySchema = z.object({
  filterGroups: z.array(visitCounselSearchFilterGroupSchema),
  sortBy: visitCounselSortByFieldSchema,
  sortOrder: z.enum(['ASC', 'DESC']),
  pageSize: z.number().int().positive().max(100),
  cursor: z.string().nullable().optional(),
});

/**
 * VisitCounselSearchView 스키마
 */
export const visitCounselSearchViewSchema = z.object({
  items: z.array(visitCounselViewSchema),
  hasNext: z.boolean(),
  nextCursor: z.string().nullable(),
  count: z.number().int().nonnegative(),
});

/**
 * VisitCounselEvent 스키마
 */
export const visitCounselEventSchema = z
  .object({
    id: z.string(),
    eventType: z.string(),
    occurredAt: z.string().datetime(),
    aggregateId: z.string().uuid(),
    sequence: z.number().int().nonnegative(),
  })
  .passthrough(); // 추가 필드 허용

/**
 * VisitCounselEventViewItem 스키마
 */
export const visitCounselEventViewItemSchema = z.object({
  event: visitCounselEventSchema,
  checkpoint: z.number().int(),
});

/**
 * VisitCounselEventView 스키마
 */
export const visitCounselEventViewSchema = z.object({
  visitCounselId: z.string().uuid(),
  events: z.array(visitCounselEventViewItemSchema),
});

// ============================================================================
// Type Inference from Schemas
// ============================================================================

export type VisitCounselViewInput = z.infer<typeof visitCounselViewSchema>;
export type VisitCounselSearchFilterInput = z.infer<typeof visitCounselSearchFilterSchema>;
export type VisitCounselSearchFilterGroupInput = z.infer<
  typeof visitCounselSearchFilterGroupSchema
>;
export type VisitCounselSearchQueryInput = z.infer<typeof visitCounselSearchQuerySchema>;
export type VisitCounselSearchViewInput = z.infer<typeof visitCounselSearchViewSchema>;
export type VisitCounselEventInput = z.infer<typeof visitCounselEventSchema>;
export type VisitCounselEventViewItemInput = z.infer<typeof visitCounselEventViewItemSchema>;
export type VisitCounselEventViewInput = z.infer<typeof visitCounselEventViewSchema>;
